import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import FullscreenIcon from "@material-ui/icons/FullscreenOutlined";

// Amostra dos dados que o coletor pegou (saída do nó "Retornar ao coletor").
// Mostra uma pré-visualização compacta (poucas linhas) e, ao clicar, abre um
// modal tipo Excel com TODAS as colunas e a amostra completa. Não fica no escuro.
const useStyles = makeStyles(theme => ({
  wrap: { marginTop: 4 },
  head: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  count: { fontSize: 12.5, fontWeight: 700, color: theme.app.ink },
  hint: { fontSize: 11.5, color: theme.app.textTertiary },
  grow: { flexGrow: 1 },
  preview: { border: `1px solid ${theme.app.border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main } },
  scroll: { overflowX: "auto" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 12, fontFamily: theme.app.mono },
  th: { textAlign: "left", padding: "6px 10px", background: theme.app.lineSoft, color: theme.app.textSecondary, fontWeight: 700, whiteSpace: "nowrap", borderBottom: `1px solid ${theme.app.border}`, position: "sticky", top: 0 },
  td: { padding: "5px 10px", borderBottom: `1px solid ${theme.app.lineSoft}`, whiteSpace: "nowrap", color: theme.app.ink, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" },
  expand: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "6px 10px", fontSize: 11.5, fontWeight: 600, color: theme.palette.primary.main, background: theme.app.hover, borderTop: `1px solid ${theme.app.lineSoft}` },
  empty: { fontSize: 12.5, color: theme.app.textTertiary, padding: "8px 0" },
  modalScroll: { overflow: "auto", maxHeight: "72vh", border: `1px solid ${theme.app.border}`, borderRadius: 8 },
  rownum: { color: theme.app.textTertiary, textAlign: "right", userSelect: "none" },
}));

// células: objeto/array vira JSON; null/undefined = vazio; resto = texto
const fmt = v => {
  if (v == null) return "";
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

const CollectorSample = ({ rows, count, truncated, cursorField }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const list = Array.isArray(rows) ? rows : [];

  // colunas = união das chaves preservando a ordem de aparição
  const cols = useMemo(() => {
    const seen = [];
    list.forEach(r => Object.keys(r || {}).forEach(k => { if (!seen.includes(k)) seen.push(k); }));
    return seen;
  }, [list]);

  if (!list.length) {
    return <div className={classes.empty}>Nenhum registro nesta execução.</div>;
  }

  const PREVIEW_ROWS = 4;
  const preview = list.slice(0, PREVIEW_ROWS);

  const renderTable = (data, full) => (
    <table className={classes.table}>
      <thead>
        <tr>
          {full && <th className={classes.th} style={{ width: 40 }}>#</th>}
          {cols.map(c => (
            <th key={c} className={classes.th}>
              {c}{c === cursorField ? " 🔑" : ""}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i}>
            {full && <td className={`${classes.td} ${classes.rownum}`}>{i + 1}</td>}
            {cols.map(c => (
              <td key={c} className={classes.td} title={fmt(r && r[c])}>{fmt(r && r[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className={classes.wrap}>
      <div className={classes.head}>
        <span className={classes.count}>{count} registro{count === 1 ? "" : "s"}</span>
        <span className={classes.hint}>· {cols.length} coluna{cols.length === 1 ? "" : "s"}{cursorField ? ` · cursor: ${cursorField}` : ""}{truncated ? " · amostra" : ""}</span>
        <span className={classes.grow} />
      </div>

      <div className={classes.preview} onClick={() => setOpen(true)} title="Ver todos os dados">
        <div className={classes.scroll}>{renderTable(preview, false)}</div>
        <div className={classes.expand}>
          <FullscreenIcon style={{ fontSize: 15 }} />
          Ver {list.length > PREVIEW_ROWS ? `todas as ${list.length} linhas` : "em tela cheia"}
        </div>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Dados do coletor — {count} registro{count === 1 ? "" : "s"}
          {truncated ? ` (amostra de ${list.length})` : ""} · {cols.length} coluna{cols.length === 1 ? "" : "s"}
        </DialogTitle>
        <DialogContent>
          <div className={classes.modalScroll}>{renderTable(list, true)}</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CollectorSample;
